import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useApiClient } from "@/context/ApiContext";
import { CurrentUserContext } from "@/context/CurrentUserContext";
import { getPostings } from "@/services/staffing/Staffing";
import { Posting } from "@/types/staffing/posting";
import { Loader2, Plus } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import ServiceRequestForm from "./ServiceRequestForm";

export default function ServiceRequestWidget() {
  const [showForm, setShowForm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [postings, setPostings] = useState<Posting[]>([]);
  const [loading, setLoading] = useState(true);
  const apiClient = useApiClient();
  const { currentUser } = useContext(CurrentUserContext);

  useEffect(() => {
    if (!currentUser.FullName) return;
    setLoading(true);
    getPostings({ filter: `ServiceRequestedBy eq 'positive\\${currentUser.LoginName}'`, orderby: "Created desc", expand: ["Client"] }, apiClient)
      .then((res) => {
        const all: Posting[] = res.data?.value ?? res.data ?? [];
        setPostings(
          all.filter(
            (p) =>
              p.ServiceRequestedBy === "positive\\" + currentUser.LoginName,
          ),
        );
      })
      .catch(() => setPostings([]))
      .finally(() => setLoading(false));
  }, [currentUser.FullName, currentUser.LoginName, apiClient, refreshKey]);

  if (showForm) {
    return (
      <ServiceRequestForm
        onBack={() => {
          setShowForm(false);
          setRefreshKey((k) => k + 1);
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center justify-between">
        <Button onClick={() => setShowForm(true)} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          New Service Request
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading requests…
        </div>
      ) : postings.length === 0 ? (
        <div className="text-sm text-muted-foreground">
          No service requests yet.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {postings.map((p) => (
            <div
              key={p.Id}
              className="rounded-md border border-border bg-card px-3 py-2.5 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {p.Client?.Name ?? `Client ${p.ClientId}`}
                </p>
                <p className="text-xs text-muted-foreground">
                  {p.ServiceType.replace(/;#/g, "").trim()} ·{" "}
                  {new Date(p.Created).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Badge
                  variant="outline"
                  className={`text-xs ${p.Status === "Active" ? "border-green-400 bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400" : ""}`}
                >
                  {p.Status}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
